import { useEffect, useState } from 'react';
import {
  Box,
  HStack,
  Text,
  VStack,
  useMediaQuery,
  Pressable,
  Avatar
} from 'native-base';
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from '../../utils/fonts';
import { COLORS } from '../../utils/colors';
import HotelDetailsCard from './detailsCard';
import { editIconDetailsPage, deleteCardsIconSVG, partnerMailSVG } from '../../utils/svg';
import { MOBILE_VIEW_WIDTH } from '../../utils/screens';
import CustomButton from '../../components/button';
import CustomModal from '../../components/modal';
import { Toaster } from '../../components/toaster';
import MobileModelSheet from '../../components/actionSheet';
import DividerLine from '../../components/divider';
import QuickMeetModal from '../../pages/quickMeetDetailPage/modalChildren/quickMeetModal';
import EmailEnquiryModal from '../../pages/quickMeetDetailPage/modalChildren/emailEnquiryModal';
import HotelTable from '../../components/HotelTable';
import { LoaderAnimation } from '../../components/spinner';
import NoDataFound from '../../components/noDataFound';
import {
  addHotelDetailsAPI,
  editHotelDetailsAPI,
  deleteHotelDetailsAPI
} from '../../api/apis/quicklinks';

const HotelDetails = ({
  scrollToTop = () => {},
  refreshHotelList,
  hotelList = [],
  hasMore = false,
  isLoadingMore = false,
  onLoadMore = () => {},
  pagination = null
}) => {
  const toaster = Toaster();
  const [MobileWindow] = useMediaQuery({
    maxWidth: MOBILE_VIEW_WIDTH
  });

  const [showMeetModal, setShowMeetModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [currentHotelId, setCurrentHotelId] = useState(null);
  const [currentHotelName, setCurrentHotelName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedHotelIds, setSelectedHotelIds] = useState([]);
  const [resetSelection, setResetSelection] = useState(false);

  const handleEditClick = (hotelData) => {
    scrollToTop();
    setEditData(hotelData);
    setCurrentHotelId(hotelData?._id);
    setShowMeetModal(true);
  };

  const handleDeleteClick = (hotelData) => {
    scrollToTop();
    setCurrentHotelId(hotelData?._id);
    setCurrentHotelName(hotelData?.hotel_name || 'this hotel');
    setShowDeleteModal(true);
  };

  const handleEmailClick = (hotelIds) => {
    setSelectedHotelIds(hotelIds);
    setShowEmailModal(true);
  };

  const handleEmailSuccess = () => {
    // Reset selections after successful email enquiry
    setResetSelection(true);
    // Reset the flag after a short delay
    setTimeout(() => setResetSelection(false), 100);
  };

  const handleHotelSubmit = async (hotelData) => {
    try {
      LoaderAnimation.show();
      let response;
      
      if (editData) {
        // Update existing hotel
        response = await editHotelDetailsAPI(currentHotelId, hotelData);
        setIsEditing(true);
      } else {
        // Add new hotel
        response = await addHotelDetailsAPI(hotelData);
      }

      if (response.error) {
        toaster(
          response.message || `Failed to ${editData ? 'update' : 'add'} hotel details`,
          'error',
          'error'
        );
      } else {
        toaster(
          response.data?.data?.message || `Hotel details ${editData ? 'updated' : 'added'} successfully`,
          'success',
          'success'
        );
        setShowMeetModal(false);
        setEditData(null);
        setCurrentHotelId(null);
        setIsEditing(false);
        // Refresh the hotel list
        setTimeout(() => {
          refreshHotelList();
        }, 300);
      }
    } catch (error) {
      console.error(`Error ${editData ? 'updating' : 'adding'} hotel details:`, error);
      toaster(`Error ${editData ? 'updating' : 'adding'} hotel details`, 'error', 'error');
    } finally {
      LoaderAnimation.hide();
    }
  };

  const handleDeleteHotel = async () => {
    try {
      setIsDeleting(true);
      LoaderAnimation.show();
      
      const response = await deleteHotelDetailsAPI(currentHotelId);
      
      if (response.error) {
        toaster(
          response.message || 'Failed to delete hotel',
          'error',
          'error'
        );
      } else {
        toaster(
          response.data?.data?.message || 'Hotel deleted successfully',
          'success',
          'success'
        );
        setShowDeleteModal(false);
        setCurrentHotelId(null);
        setCurrentHotelName('');
        // Refresh the hotel list
        setTimeout(() => {
          refreshHotelList();
        }, 300);
      }
    } catch (error) {
      console.error('Error deleting hotel:', error);
      toaster('Error deleting hotel', 'error', 'error');
    } finally {
      setIsDeleting(false);
      LoaderAnimation.hide();
    }
  };

  const getDeleteModalBody = () => {
    return (
      <Box>
        <Text
          fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
          fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
        >
          Are you sure you want to delete "
          {currentHotelName}"?
        </Text>
      </Box>
    );
  };

  const getDeleteModalFooter = () => {
    return (
      <HStack
        justifyContent={'end'}
        width={'100%'}
        space={4}
      >
        <CustomButton
          onPress={() => setShowDeleteModal(false)}
          text='Cancel'
          color={COLORS.MODAL_BACK_BUTTON_COLOR}
          variant='outline'
        />
        <CustomButton
          onPress={handleDeleteHotel}
          text={isDeleting ? 'Deleting...' : 'Delete'}
          color={COLORS.WHITE_COLOR}
          backgroundColor={COLORS.REJECT_TEXT_COLOR}
          isLoading={isDeleting}
        />
      </HStack>
    );
  };

  // If no hotels, show a message or return empty
  if (!hotelList || hotelList.length === 0) {
    return (
      <Box p={4}>
        <NoDataFound dataName="hotel details" />
      </Box>
    );
  }

  return (
    <>
      <VStack space={4} w={'100%'}>
        <HotelTable
          hotelList={hotelList}
          onEditHotel={handleEditClick}
          onDeleteHotel={handleDeleteClick}
          onEmailEnquiry={handleEmailClick}
          refreshHotelList={refreshHotelList}
          scrollToTop={scrollToTop}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={onLoadMore}
          resetSelection={resetSelection}
          pagination={pagination}
        />
      </VStack>

      <QuickMeetModal
        isOpen={showMeetModal}
        onClose={() => {
          setShowMeetModal(false);
          setEditData(null);
          setCurrentHotelId(null);
          setIsEditing(false);
        }}
        onSubmit={handleHotelSubmit}
        editData={editData}
        isEditing={isEditing}
      />

      <EmailEnquiryModal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setSelectedHotelIds([]);
        }}
        hotelIds={selectedHotelIds}
        hotelList={hotelList}
        onSuccess={handleEmailSuccess}
      />

      {/* Delete Confirmation Modal */}
      {!MobileWindow ? (
        <CustomModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          header="Delete Hotel"
          body={getDeleteModalBody()}
          footer={getDeleteModalFooter()}
          width="40%"
        />
      ) : (
        <MobileModelSheet
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          header="Delete Hotel"
          body={getDeleteModalBody()}
          footer={getDeleteModalFooter()}
          maxH="40vh"
        />
      )}
    </>
  );
};

export default HotelDetails;
